import { invokeAgent as invokeAgentQuery } from "@/graphql/queries";
import { invokeQmaAutomation } from "@/graphql/invokeAgent";
import { onQmaAutomationResponse } from "@/graphql/subscriptions";
import { DEFAULT_MODEL_KEY, resolveModelKey } from "@/constants/modelRegistry";
import { PROCESS_STATUS } from "@/store/subscriptionCore/processConstants";
import { invokeAndSubscribe } from "@/store/subscriptionCore/subscriptionApi";
import { propertyIdToDetailsPath } from "@/utils/propertyUrlUtils";

/**
 * @param {Object} params
 * @param {string} params.propertyId
 * @param {string} params.userId
 * @param {string} params.customerId
 * @param {string} [params.modelName]
 * @param {Array<string>|null} [params.comps]
 * @param {string|null} [params.compsAnalysis]
 * @param {"rerun"} [params.mode] - "rerun" reuses the last report's web searches while they are fresh
 * @param {Object} context
 * @returns {Promise<Object>}
 */
export async function runQmaProcess(params, context) {
  const {
    propertyId,
    userId,
    customerId,
    modelName = DEFAULT_MODEL_KEY,
    comps = null,
    compsAnalysis = null,
    mode,
  } = params;
  const resolvedModelName = resolveModelKey(modelName, DEFAULT_MODEL_KEY);
  const isRerun = mode === "rerun";

  context.setProgress({
    status: PROCESS_STATUS.INVOKING,
    message: isRerun ? "Updating QMA report..." : "Starting QMA generation...",
  });

  const { correlationId, payload } = await invokeAndSubscribe({
    invokeQuery: invokeAgentQuery,
    invokeVariables: invokeQmaAutomation({
      propertyId,
      userId,
      customerId,
      modelName: resolvedModelName,
      comps,
      compsAnalysis,
      mode,
    }),
    invokeResultPath: "data.invokeAgent",
    subscriptionQuery: onQmaAutomationResponse,
    processTag: "QMA",
    isProcessingEvent: (event) => {
      if (event?.status === "processing") {
        context.setProgress({
          status: PROCESS_STATUS.PROCESSING,
          message: isRerun ? "Updating QMA report..." : "Generating QMA report...",
        });
        return true;
      }
      return false;
    },
  });

  context.setCorrelationId(correlationId);

  const propertyActionPath = propertyIdToDetailsPath(propertyId);

  return {
    correlationId,
    payload,
    notification: {
      outcome: "success",
      message: isRerun ? "QMA report was updated." : "QMA report was generated successfully.",
      actionRoute: propertyActionPath,
      actionLabel: propertyActionPath ? "View Property" : null,
    },
  };
}
